import React, { useState, useEffect, useMemo } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';

import { naturalStringSort, studentSort } from 'components/utils/sort';
import { Clazz, Level, School, ScopeConstraints, Student, StudentRoster, StudentAudienceProps, TeachingGroup, ParentAudienceProps } from '../../../../../../../models/FormProps';
import { isEmpty, isNotEmpty } from '../../../../../../../utils/equality';
import SchoolSelector from '../../SchoolSelector';
import { allowsSchoolWideSelection, scopeStudentRoster } from '../scope';
import StudentAudienceGroupSelectorContainer from './StudentAudienceGroupSelectorContainer';

interface Props {
  school: School;
  schools: Array<School>;
  scopeConstraints: ScopeConstraints | null;
  viewableAudience: StudentAudienceProps | ParentAudienceProps;
  addViewableEntitiesCallback: (viewableEntities: Array<School | Level | Clazz | TeachingGroup | Student>) => void;
  changeAcademicYearCallback: (year: number) => void;
  viewOnly: boolean;
  isHQUser: boolean;
  getStudentRosterUrl: string;
  enableTeachingGroups: boolean;
  scrollContainerRef: React.MutableRefObject<HTMLDivElement>;
  isCustodianAudience?: boolean;
}

export default function StudentAudienceSelector(props: Props): React.ReactElement {
  const {
    school,
    schools,
    scopeConstraints,
    viewableAudience,
    addViewableEntitiesCallback,
    changeAcademicYearCallback,
    viewOnly,
    isHQUser,
    getStudentRosterUrl,
    enableTeachingGroups,
    scrollContainerRef,
    isCustodianAudience
  } = props;
  const [selectedSchoolID, setSelectedSchoolID] = useState<number>(school?.entity_id);
  const [studentRoster, setStudentRoster] = useState<StudentRoster>(null);

  // The constraint is read here, once, and everything below this point receives
  // what it decided rather than the constraint itself - so the two selectors
  // that offer a whole school cannot come to different conclusions about it, and
  // the level accordions, the drill-downs into a level and the search results
  // are all drawn from the same in-scope roster.
  //
  // The fetched roster is left whole in state and narrowed on the way out, so
  // the constraint stays a fact about what is rendered rather than something
  // baked into what was fetched. There is no flash of a level about to be
  // withdrawn: the constraint rides in with the form data, so it is already here
  // when the roster arrives.
  const scopedStudentRoster = useMemo(
    () => (studentRoster === null ? null : scopeStudentRoster(studentRoster, scopeConstraints)),
    [studentRoster, scopeConstraints]
  );
  const allowSchoolWideSelection = allowsSchoolWideSelection(scopeConstraints);

  useEffect(() => {
    if (isNotEmpty(selectedSchoolID)) getRostersBySchoolID();
  }, [selectedSchoolID, viewableAudience.year]);

  useEffect(() => () => {
    setStudentRoster(null);
    setSelectedSchoolID(null);
  }, []);

  return (
    <Stack spacing={2} component={Paper} className="audience-editor-audience-selector">
      {isEmpty(selectedSchoolID) && isHQUser ? renderSchoolsSelector() : renderAudienceGroupSelector()}
    </Stack>
  );

  function renderSchoolsSelector() {
    if (isEmpty(schools)) {
      return <CircularProgress className="circular" />;
    }

    return (
      <SchoolSelector
        schools={schools}
        allowSchoolWideSelection={allowSchoolWideSelection}
        viewOnly={viewOnly}
        addViewableEntitiesCallback={addViewableEntitiesCallback}
        setSchoolIDCallback={setSelectedSchoolID}
      />
    );
  }

  function renderAudienceGroupSelector() {
    if (isEmpty(studentRoster)) {
      return <CircularProgress className="circular" />;
    }

    return (
      <StudentAudienceGroupSelectorContainer
        scrollContainerRef={scrollContainerRef}
        viewableAudience={viewableAudience}
        studentRoster={scopedStudentRoster}
        allowSchoolWideSelection={allowSchoolWideSelection}
        isHQUser={isHQUser}
        viewOnly={viewOnly}
        addViewableEntitiesCallback={addViewableEntitiesCallback}
        changeAcademicYearCallback={changeAcademicYearCallback}
        setSchoolIDCallback={setSelectedSchoolID}
        enableTeachingGroups={enableTeachingGroups}
        isCustodianAudience={isCustodianAudience}
      />
    );
  }

  function getRostersBySchoolID() {
    setStudentRoster(null);

    const url = new URL(getStudentRosterUrl);
    url.search = new URLSearchParams({
      school_id: selectedSchoolID.toString(),
      year: viewableAudience.year.toString()
    }).toString();

    const requestOptions = {
      method: 'GET'
    };

    // SSRF Safe: URL base is controlled (getStudentRosterUrl prop from server),
    // only query parameters are user-controlled and properly encoded by URLSearchParams
    return fetch(url.toString(), requestOptions)
      .then(response => response.json())
      .then(data => {
        if (data) {
          const roster = data.student_roster;
          roster.levels.sort((a, b) => a.code - b.code);
          roster.clazzes.sort((a, b) => naturalStringSort(a.name, b.name));
          roster.students.sort((a, b) => studentSort(a, b));
          roster.students = roster.students.map(student => ({
            ...student,
            teaching_group_ids: new Set(student.teaching_group_ids)
          }));

          setStudentRoster(data.student_roster);
        }
      });
  }
}
